import React from "react";
import { useTranslation } from "react-i18next";
import { BellRing, Lock } from "lucide-react";

interface AutoRemindersToggleProps {
  /** État courant (contrôlé par le parent). */
  checked: boolean;
  /** Le parent persiste (RPC) ou garde en mémoire jusqu'à la création. */
  onChange: (next: boolean) => void;
  /** Sans abonnement Solo (ou déconnecté) : cadenas + lien tarifs. */
  locked: boolean;
  /** Enregistrement en cours. */
  busy?: boolean;
  testId?: string;
}

/**
 * Interrupteur « Relancer automatiquement les invités » (plan Solo).
 *
 * Même composant aux deux endroits décidés avec Julien le 2026-09-18 :
 * au moment où l'on ajoute des invités (création du sondage et dialogue
 * d'ajout), et sur la page Invitations où il reste modifiable.
 *
 * La relance MANUELLE (bouton « Relancer ») et le rappel créateur J+2
 * restent gratuits : seule l'automatisation est payante.
 *
 * Verrouillage uniquement visuel — la vérification qui compte est côté base
 * (set_poll_auto_reminders + gate revérifié à chaque envoi).
 */
export const AutoRemindersToggle: React.FC<AutoRemindersToggleProps> = ({
  checked,
  onChange,
  locked,
  busy = false,
  testId = "auto-reminders-toggle",
}) => {
  const { t } = useTranslation(["polls"]);

  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-1.5">
      {/* Deux lignes en mobile, une seule à partir de sm. En 320 px sur une
          seule ligne, `truncate` donnait « Relancer a… » et, sans troncature,
          le titre poussait l'interrupteur hors de l'écran (constaté en
          preview le 2026-09-21). */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-start gap-2 text-foreground min-w-0">
          <BellRing className="h-4 w-4 shrink-0 mt-0.5 sm:mt-0" />
          <span className="text-sm font-medium">
            {t("polls:invitations.auto_reminders.title")}
          </span>
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/40 shrink-0">
            {t("polls:invitations.auto_reminders.solo_badge")}
          </span>
        </div>

        {locked ? (
          // Ancre volontaire, pas <Link> : ce bloc s'affiche aussi dans des
          // dialogues montés hors routeur (tests unitaires compris).
          <a
            href="/pricing"
            className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 shrink-0"
            data-testid={`${testId}-upgrade`}
          >
            <Lock className="h-3.5 w-3.5" />
            {t("polls:invitations.auto_reminders.upgrade_cta")}
          </a>
        ) : (
          <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={t("polls:invitations.auto_reminders.title")}
            disabled={busy}
            onClick={() => onChange(!checked)}
            data-testid={testId}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
              checked ? "bg-primary" : "bg-muted"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-background transition-transform ${
                checked ? "translate-x-[18px]" : "translate-x-[3px]"
              }`}
            />
          </button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {locked
          ? t("polls:invitations.auto_reminders.locked_description")
          : t("polls:invitations.auto_reminders.description")}
      </p>
    </div>
  );
};

export default AutoRemindersToggle;
